import { useEffect, useRef } from 'react';

interface ShortcutCheatSheetProps {
  onClose: () => void;
}

interface ShortcutEntry {
  keys: string[];
  description: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: ShortcutEntry[];
}

const sections: ShortcutSection[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['g', 'a'], description: 'Go to Analyses' },
      { keys: ['g', 'f'], description: 'Go to Flip Calculator' },
      { keys: ['g', 'l'], description: 'Go to Listings' },
      { keys: ['g', 'p'], description: 'Go to Projects' },
      { keys: ['g', 'i'], description: 'Go to Import' },
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['n'], description: 'New analysis' },
      { keys: ['?'], description: 'Show/hide this help' },
      { keys: ['Esc'], description: 'Close this dialog' },
    ],
  },
  {
    title: 'Analysis Detail',
    shortcuts: [
      { keys: ['1'], description: 'Cost Estimates tab' },
      { keys: ['2'], description: 'Comparables tab' },
      { keys: ['3'], description: 'Investment tab' },
      { keys: ['4'], description: 'Condition tab' },
      { keys: ['5'], description: 'Permits & Zoning tab' },
      { keys: ['6'], description: 'Market Intel tab' },
      { keys: ['7'], description: 'AI Narrative tab' },
      { keys: ['8'], description: 'History tab' },
    ],
  },
  {
    title: 'Flip Calculator',
    shortcuts: [
      { keys: ['Ctrl', 'z'], description: 'Undo input change' },
      { keys: ['Ctrl', 'Shift', 'z'], description: 'Redo input change' },
    ],
  },
];

export default function ShortcutCheatSheet({ onClose }: ShortcutCheatSheetProps) {
  const backdropRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Close when clicking backdrop
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      onClose();
    }
  };

  return (
    <div
      className="shortcut-cheatsheet-backdrop"
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div className="shortcut-cheatsheet-modal">
        <div className="shortcut-cheatsheet-header">
          <h2>Keyboard Shortcuts</h2>
          <button
            className="shortcut-cheatsheet-close"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
          >
            &times;
          </button>
        </div>

        <div className="shortcut-cheatsheet-body">
          {sections.map((section) => (
            <div key={section.title} className="shortcut-section">
              <h3 className="shortcut-section-title">{section.title}</h3>
              <div className="shortcut-list">
                {section.shortcuts.map((shortcut) => (
                  <div key={shortcut.description} className="shortcut-row">
                    <span className="shortcut-keys">
                      {shortcut.keys.map((key, i) => (
                        <span key={i}>
                          {i > 0 && (
                            <span className="shortcut-key-separator">
                              {section.title === 'Navigation' ? 'then' : '+'}
                            </span>
                          )}
                          <kbd className="kbd">{key}</kbd>
                        </span>
                      ))}
                    </span>
                    <span className="shortcut-description">{shortcut.description}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="shortcut-cheatsheet-footer">
          Press <kbd className="kbd">?</kbd> to toggle this dialog
        </div>
      </div>
    </div>
  );
}
